import React, { useEffect, useState } from 'react';
import { adminDonationService } from '../services';
import { FaEdit, FaTrash } from 'react-icons/fa';
import { resolveMediaUrl } from '../services/api';
import { notifyError, notifyInfo, notifySuccess } from '../utils/toast';

const Donations = () => {
    const [donations, setDonations] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        fetchDonations();
    }, []);

    const fetchDonations = async () => {
        try {
            const response = await adminDonationService.getAll();
            setDonations(response.data.data);
        } catch (error) {
            console.error('Error fetching donations:', error);
            notifyError('Unable to load donations.');
        } finally {
            setLoading(false);
        }
    };

    const updateStatus = async (id, status) => {
        try {
            await adminDonationService.updateStatus(id, status);
            fetchDonations();
            notifySuccess(`Donation marked as ${status}.`);
        } catch (error) {
            console.error('Error updating donation:', error);
            notifyError('Unable to update donation status.');
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm('Delete this donation record?')) return;
        try {
            await adminDonationService.delete(id);
            notifySuccess('Donation deleted successfully.');
            await fetchDonations();
        } catch (error) {
            notifyError('Unable to delete donation.');
        }
    };

    const totalAmount = donations
        .filter(d => d.status === 'completed')
        .reduce((sum, d) => sum + parseFloat(d.amount || 0), 0);

    const paymentMethodLabel = (method) => {
        const labels = {
            easypaisa: 'Easypaisa',
            jazzcash: 'JazzCash',
            bank_transfer: 'Bank Transfer',
            cash: 'Cash',
        };
        return labels[method] || method || '-';
    };

    return (
        <div>
            <h1 className="text-4xl font-bold text-gray-800 mb-8">Donations</h1>

            {/* Summary Cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
                <div className="bg-white p-6 rounded-lg shadow-lg">
                    <p className="text-gray-600 text-sm">Total Donations</p>
                    <p className="text-3xl font-bold text-emerald-600">PKR {totalAmount.toLocaleString()}</p>
                </div>
                <div className="bg-white p-6 rounded-lg shadow-lg">
                    <p className="text-gray-600 text-sm">Completed</p>
                    <p className="text-3xl font-bold text-green-600">{donations.filter(d => d.status === 'completed').length}</p>
                </div>
                <div className="bg-white p-6 rounded-lg shadow-lg">
                    <p className="text-gray-600 text-sm">Pending</p>
                    <p className="text-3xl font-bold text-yellow-600">{donations.filter(d => d.status === 'pending').length}</p>
                </div>
            </div>

            {/* Donations Table */}
            {loading ? (
                <div className="text-center py-12">
                    <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600"></div>
                </div>
            ) : (
                <div className="bg-white rounded-lg shadow-lg overflow-hidden">
                    <div className="overflow-x-auto">
                        <table className="w-full">
                            <thead className="bg-emerald-600 text-white">
                                <tr>
                                    <th className="px-6 py-3 text-left">Donor Name</th>
                                    <th className="px-6 py-3 text-left">Amount</th>
                                    <th className="px-6 py-3 text-left">Currency</th>
                                    <th className="px-6 py-3 text-left">Method</th>
                                    <th className="px-6 py-3 text-left">Proof</th>
                                    <th className="px-6 py-3 text-left">Status</th>
                                    <th className="px-6 py-3 text-left">Date</th>
                                    <th className="px-6 py-3 text-left">Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {donations.map((donation) => (
                                    <tr key={donation.id} className="border-b hover:bg-gray-50">
                                        <td className="px-6 py-4">{donation.donor?.name}</td>
                                        <td className="px-6 py-4">{donation.amount}</td>
                                        <td className="px-6 py-4">{donation.currency}</td>
                                        <td className="px-6 py-4">{paymentMethodLabel(donation.payment_method)}</td>
                                        <td className="px-6 py-4">
                                            {donation.payment_screenshot ? (
                                                <a
                                                    href={resolveMediaUrl(donation.payment_screenshot)}
                                                    target="_blank"
                                                    rel="noreferrer"
                                                    className="text-emerald-600 hover:underline"
                                                >
                                                    View
                                                </a>
                                            ) : (
                                                <span className="text-gray-400">-</span>
                                            )}
                                        </td>
                                        <td className="px-6 py-4">
                                            <select
                                                value={donation.status}
                                                onChange={(e) => updateStatus(donation.id, e.target.value)}
                                                className={`px-3 py-1 rounded-full text-white text-xs font-semibold border-none cursor-pointer ${donation.status === 'completed' ? 'bg-green-500' :
                                                        donation.status === 'pending' ? 'bg-yellow-500' :
                                                            donation.status === 'failed' ? 'bg-red-500' :
                                                                'bg-gray-500'
                                                    }`}
                                            >
                                                <option value="pending">Pending</option>
                                                <option value="completed">Completed</option>
                                                <option value="failed">Failed</option>
                                                <option value="refunded">Refunded</option>
                                            </select>
                                        </td>
                                        <td className="px-6 py-4">{new Date(donation.created_at).toLocaleDateString()}</td>
                                        <td className="px-6 py-4 flex gap-3">
                                            <button
                                                onClick={() => notifyInfo('Use the status dropdown to edit this donation.')}
                                                className="text-emerald-600 hover:text-emerald-800"
                                                title="Edit"
                                            >
                                                <FaEdit />
                                            </button>
                                            <button
                                                onClick={() => handleDelete(donation.id)}
                                                className="text-red-600 hover:text-red-800"
                                                title="Remove"
                                            >
                                                <FaTrash />
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Donations;
